/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { RichText } from 'prismic-reactjs';

import BulmaContainer from 'react-bulma-components/lib/components/container';
import BulmaSection from 'react-bulma-components/lib/components/section';
import BulmaTable from 'react-bulma-components/lib/components/table';
import { linkResolver } from '../../util/linkResolver';

import { Heading, LazyImage } from '../atoms';
import { createRandomId } from '../../util/createRandomId';
import { checkCookies } from '../../util/checkCookies';

import tooltipicon from '../../assets/images/tooltipicon.svg';

import {
  styledSection,
  StyledHeadingContainer,
  styledHeading,
  StyledParagraph,
  StyledToolTip,
  StyledToolTipText,
  styledTable,
  styledLink,
  StyledPopularPill,
  StyledSmallText,
  StyledManagedDatabase,
} from './PricingTableStyles';

const renderHeading = (text, atts) => (
  <Heading {...atts} css={atts.css}>
    {text.text}
  </Heading>
);

const onButtonClick = event => {
  const dataName = event.currentTarget.dataset.name;
  if (dataName) {
    window.optimizely.push({
      type: 'event',
      eventName: 'clicked_signup_cta',
      tags: {
        action: 'click',
        category: 'pricing',
        name: dataName,
      },
    });

    window.analytics.track('Web Interaction', {
      action: 'click',
      category: 'pricing',
      name: dataName,
    });
  }
};

const LinkDisplay = ({ condition, styleLink, randomId, rowLink, rowText, notLoggedInLink, notLoggedInText }) =>
  condition ? (
    <a css={styleLink} id={randomId} href={rowLink ? rowLink.url : ''}>
      <span
        data-name={rowText}
        onClick={onButtonClick}
        onKeyDown={onButtonClick}
        role="button"
        tabIndex={createRandomId()}
      >
        {rowText}
      </span>
    </a>
  ) : (
    <a css={styleLink} id={randomId} href={notLoggedInLink ? notLoggedInLink.url : ''}>
      <span
        data-name={notLoggedInText}
        onClick={onButtonClick}
        onKeyDown={onButtonClick}
        role="button"
        tabIndex={createRandomId()}
      >
        {notLoggedInText}
      </span>
    </a>
  );

const PricingTable = ({ input }) => {
  const {
    heading,
    heading_color,
    subheading,
    subheading_color,
    tooltip_text,
    target_name,
    small_text_header,
    small_text_body,
    managed_database,
  } = input.primary;
  const { fields } = input;

  const forbiddenHeaders = ['basic', 'droplet', 'mongodb', 'mySQL', 'postgreSQL', 'redis', 'storage-optimized'];
  const headers = Object.values(fields[0]).filter(header => !forbiddenHeaders.includes(header));
  const rowArr = fields.map(row => row).slice(1);
  const [isClient, setClient] = useState(false);
  const [currentDatabaseTab, setCurrentDatabaseTab] = useState('mongodb');
  const [currentDropletTab, setCurrentDropletTab] = useState('basic');
  const [managedDatabaseObj, setManagedDatabaseObj] = useState({});

  useEffect(() => {
    setClient(true);
  }, []);

  useEffect(() => {
    if (managed_database) {
      fields.forEach(item => {
        if (!managedDatabaseObj[item.database] && item.database && item.droplet) {
          managedDatabaseObj[item.database] = { [item.droplet]: [{ ...item }] };
        } else if (managedDatabaseObj[item.database] && !managedDatabaseObj[item.database][item.droplet]) {
          managedDatabaseObj[item.database][item.droplet] = [{ ...item }];
        } else if (managedDatabaseObj[item.database] && managedDatabaseObj[item.database][item.droplet]) {
          managedDatabaseObj[item.database][item.droplet].push({ ...item });
        }
      });
    }
  }, []);

  const renderDropletPricingTable = rows => (
    <BulmaTable css={styledTable} className="is-scrollable">
      <thead>
        <tr>
          {headers &&
            headers.map(col => (col && !forbiddenHeaders.includes(col) ? <th key={createRandomId()}>{col}</th> : null))}
          <th key={createRandomId()} />
        </tr>
      </thead>
      <tbody>
        {rows &&
          rows.length > 0 &&
          rows.slice(1).map(row => (
            <tr>
              {row.column_text_1 && (
                <td>
                  {row.column_text_1}
                  {row.popular_column && <StyledPopularPill>Popular</StyledPopularPill>}
                </td>
              )}
              {row.column_text_2 && <td>{row.column_text_2}</td>}
              {row.column_text_3 && (
                <td data-price={row.data_price_attr_one ? row.data_price_attr_one : null}>{row.column_text_3}</td>
              )}
              {row.column_text_4 && (
                <td data-price={row.data_price_attr_two ? row.data_price_attr_two : null}>{row.column_text_4}</td>
              )}
              {row.column_text_5 && <td>{row.column_text_5}</td>}
              {row.column_text_6 && (
                <td data-price={!row.column_text_7 ? row.column_text_6 : null}>{row.column_text_6}</td>
              )}
              {row.column_text_7 && <td data-price={row.column_text_7}>{row.column_text_7}</td>}
              {row.link_url && row.link_url.url ? (
                <td>
                  {isClient ? (
                    <LinkDisplay
                      condition={checkCookies('ajs_user_id')}
                      styleLink={styledLink}
                      randomId={createRandomId()}
                      rowLink={row.link_url}
                      rowText={row.link_text}
                      notLoggedInLink={row.link_url_not_logged_in}
                      notLoggedInText={row.link_text_not_logged_in}
                    />
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
      </tbody>
    </BulmaTable>
  );

  return (
    <BulmaSection css={styledSection}>
      <BulmaContainer>
        <StyledHeadingContainer id={target_name}>
          {heading &&
            heading[0].text &&
            renderHeading(heading[0], {
              style: heading_color ? { color: heading_color } : null,
              className: 'h4 darkblue',
              css: styledHeading,
              renderAs: 'h4',
            })}
          {tooltip_text && (
            <StyledToolTip>
              <LazyImage src={tooltipicon} alt="tooltip icon" /> <StyledToolTipText>{tooltip_text}</StyledToolTipText>
            </StyledToolTip>
          )}
        </StyledHeadingContainer>
        {subheading && (
          <StyledParagraph subheadingColor={subheading_color}>
            <RichText render={subheading} linkResolver={linkResolver} />
          </StyledParagraph>
        )}
        {small_text_header && (
          <StyledSmallText>
            <span>{small_text_header}</span> {small_text_body}
          </StyledSmallText>
        )}

        {managed_database && Object.keys(managedDatabaseObj).length > 1 && (
          <StyledManagedDatabase>
            <div className="databaseTabs">
              <ul>
                <li
                  className={currentDatabaseTab === 'mongodb' && 'active'}
                  onClick={() => {
                    setCurrentDropletTab('basic');
                    setCurrentDatabaseTab('mongodb');
                  }}
                >
                  <a className={currentDatabaseTab === 'mongodb' && 'active'}>MongoDB</a>
                </li>
                <li
                  className={currentDatabaseTab === 'postgreSQL' && 'active'}
                  onClick={() => {
                    setCurrentDropletTab('basic');
                    setCurrentDatabaseTab('postgreSQL');
                  }}
                >
                  <a className={currentDatabaseTab === 'postgreSQL' && 'active'}>PostgreSQL</a>
                </li>
                <li
                  className={currentDatabaseTab === 'mySQL' && 'active'}
                  onClick={() => {
                    setCurrentDropletTab('basic');
                    setCurrentDatabaseTab('mySQL');
                  }}
                >
                  <a className={currentDatabaseTab === 'mySQL' && 'active'}>MySQL</a>
                </li>
                <li
                  className={currentDatabaseTab === 'redis' && 'active'}
                  onClick={() => {
                    setCurrentDropletTab('basic');
                    setCurrentDatabaseTab('redis');
                  }}
                >
                  <a className={currentDatabaseTab === 'redis' && 'active'}>Redis</a>
                </li>
              </ul>
            </div>
            <div className="dropletsTabsContainer">
              <div className="dropletsTabs">
                <p>Droplet options:</p>
                {currentDatabaseTab === 'mongodb' ? (
                  <ul>
                    <li>
                      <a className={currentDropletTab === 'basic' && 'active'}>
                        <label>
                          <input type="radio" id="basic" checked={currentDropletTab === 'basic'} />
                          <div>
                            Basic <span>Shared vCPU</span>
                          </div>
                        </label>
                      </a>
                    </li>
                  </ul>
                ) : currentDatabaseTab === 'postgreSQL' ? (
                  <ul>
                    <li>
                      <a
                        className={currentDropletTab === 'basic' && 'active'}
                        onClick={() => setCurrentDropletTab('basic')}
                      >
                        <label>
                          <input type="radio" id="basic" checked={currentDropletTab === 'basic'} />
                          <div>
                            Basic <span>Shared vCPU</span>
                          </div>
                        </label>
                      </a>
                    </li>
                    <li>
                      <a
                        className={currentDropletTab === 'general-purpose' && 'active'}
                        onClick={() => setCurrentDropletTab('general-purpose')}
                      >
                        <label>
                          <input type="radio" id="general-purpose" checked={currentDropletTab === 'general-purpose'} />
                          <div>
                            General Purpose <span>Dedicated vCPU</span>
                          </div>
                        </label>
                      </a>{' '}
                      <span className="new">new</span>
                    </li>
                    <li>
                      <a
                        className={currentDropletTab === 'storage-optimized' && 'active'}
                        onClick={() => setCurrentDropletTab('storage-optimized')}
                      >
                        <label>
                          <input
                            type="radio"
                            id="storage-optimized"
                            checked={currentDropletTab === 'storage-optimized'}
                          />
                          <div>
                            Storage Optimized <span>Dedicated vCPU</span>
                          </div>
                        </label>
                      </a>
                      <span className="new">new</span>
                    </li>
                  </ul>
                ) : currentDatabaseTab === 'mySQL' ? (
                  <ul>
                    <li>
                      <a
                        className={currentDropletTab === 'basic' && 'active'}
                        onClick={() => setCurrentDropletTab('basic')}
                      >
                        <label>
                          <input type="radio" id="basic" checked={currentDropletTab === 'basic'} />
                          <div>
                            Basic <span>Shared vCPU</span>
                          </div>
                        </label>
                      </a>
                    </li>
                    <li>
                      <a
                        className={currentDropletTab === 'general-purpose' && 'active'}
                        onClick={() => setCurrentDropletTab('general-purpose')}
                      >
                        <label>
                          <input type="radio" id="general-purpose" checked={currentDropletTab === 'general-purpose'} />
                          <div>
                            General Purpose <span>Dedicated vCPU</span>
                          </div>
                        </label>
                      </a>{' '}
                      <span className="new">new</span>
                    </li>
                    <li>
                      <a
                        className={currentDropletTab === 'storage-optimized' && 'active'}
                        onClick={() => setCurrentDropletTab('storage-optimized')}
                      >
                        <label>
                          <input
                            type="radio"
                            id="storage-optimized"
                            checked={currentDropletTab === 'storage-optimized'}
                          />
                          <div>
                            Storage Optimized <span>Dedicated vCPU</span>
                          </div>
                        </label>
                      </a>
                      <span className="new">new</span>
                    </li>
                  </ul>
                ) : currentDatabaseTab === 'redis' ? (
                  <ul>
                    <li>
                      <a
                        className={currentDropletTab === 'basic' && 'active'}
                        onClick={() => setCurrentDropletTab('basic')}
                      >
                        <label>
                          <input type="radio" id="basic" checked={currentDropletTab === 'basic'} />
                          <div>
                            Basic <span>Shared vCPU</span>
                          </div>
                        </label>
                      </a>
                    </li>
                    <li>
                      <a
                        className={currentDropletTab === 'memory-optimized' && 'active'}
                        onClick={() => setCurrentDropletTab('memory-optimized')}
                      >
                        <label>
                          <input
                            type="radio"
                            id="memory-optimized"
                            checked={currentDropletTab === 'memory-optimized'}
                          />
                          <div>
                            Memory Optimized <span>Dedicated vCPU</span>
                          </div>
                        </label>
                      </a>
                      <span className="new">new</span>
                    </li>
                  </ul>
                ) : null}
              </div>
            </div>
            {currentDatabaseTab === 'mongodb' &&
              currentDropletTab === 'basic' &&
              renderDropletPricingTable(managedDatabaseObj.mongodb.basic)}
            {currentDatabaseTab === 'postgreSQL' &&
              currentDropletTab === 'basic' &&
              renderDropletPricingTable(managedDatabaseObj.postgreSQL.basic)}
            {currentDatabaseTab === 'postgreSQL' &&
              currentDropletTab === 'general-purpose' &&
              renderDropletPricingTable(managedDatabaseObj.postgreSQL['general-purpose'])}
            {currentDatabaseTab === 'postgreSQL' &&
              currentDropletTab === 'storage-optimized' &&
              renderDropletPricingTable(managedDatabaseObj.postgreSQL['storage-optimized'])}
            {currentDatabaseTab === 'mySQL' &&
              currentDropletTab === 'basic' &&
              renderDropletPricingTable(managedDatabaseObj.mySQL.basic)}
            {currentDatabaseTab === 'mySQL' &&
              currentDropletTab === 'general-purpose' &&
              renderDropletPricingTable(managedDatabaseObj.mySQL['general-purpose'])}
            {currentDatabaseTab === 'mySQL' &&
              currentDropletTab === 'storage-optimized' &&
              renderDropletPricingTable(managedDatabaseObj.mySQL['storage-optimized'])}
            {currentDatabaseTab === 'redis' &&
              currentDropletTab === 'basic' &&
              renderDropletPricingTable(managedDatabaseObj.redis.basic)}
            {currentDatabaseTab === 'redis' &&
              currentDropletTab === 'memory-optimized' &&
              renderDropletPricingTable(managedDatabaseObj.redis['memory-optimized'])}
          </StyledManagedDatabase>
        )}

        {!managed_database && (
          <BulmaTable css={styledTable} className="is-scrollable">
            <thead>
              <tr>
                {headers && headers.map(col => (col ? <th key={createRandomId()}>{col}</th> : null))}
                <th key={createRandomId()} />
              </tr>
            </thead>
            <tbody>
              {rowArr.map(row => (
                <tr>
                  {row.column_text_1 && (
                    <td>
                      {row.column_text_1}
                      {row.popular_column && <StyledPopularPill>Popular</StyledPopularPill>}
                    </td>
                  )}
                  {row.column_text_2 && <td>{row.column_text_2}</td>}
                  {row.column_text_3 && (
                    <td data-price={row.data_price_attr_one ? row.data_price_attr_one : null}>{row.column_text_3}</td>
                  )}
                  {row.column_text_4 && (
                    <td data-price={row.data_price_attr_two ? row.data_price_attr_two : null}>{row.column_text_4}</td>
                  )}
                  {row.column_text_5 && <td>{row.column_text_5}</td>}
                  {row.column_text_6 && (
                    <td data-price={!row.column_text_7 ? row.column_text_6 : null}>{row.column_text_6}</td>
                  )}
                  {row.column_text_7 && <td data-price={row.column_text_7}>{row.column_text_7}</td>}
                  {row.link_url && row.link_url.url ? (
                    <td>
                      {isClient ? (
                        <LinkDisplay
                          condition={checkCookies('ajs_user_id')}
                          styleLink={styledLink}
                          randomId={createRandomId()}
                          rowLink={row.link_url}
                          rowText={row.link_text}
                          notLoggedInLink={row.link_url_not_logged_in}
                          notLoggedInText={row.link_text_not_logged_in}
                        />
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </BulmaTable>
        )}
      </BulmaContainer>
    </BulmaSection>
  );
};

export default PricingTable;

LinkDisplay.propTypes = {
  condition: PropTypes.bool,
  styleLink: PropTypes.string,
  randomId: PropTypes.string,
  rowLink: PropTypes.object,
  rowText: PropTypes.string,
  notLoggedInLink: PropTypes.object,
  notLoggedInText: PropTypes.string,
};

LinkDisplay.defaultProps = {
  condition: false,
  styleLink: null,
  randomId: null,
  rowLink: null,
  rowText: null,
  notLoggedInLink: null,
  notLoggedInText: null,
};

PricingTable.propTypes = {
  input: PropTypes.object.isRequired,
};
